import Phaser from 'phaser';

import { GameAsset } from '../model/game-asset.type';
import { GameSprite } from '../model/game-sprite.type';

type Dimension = { width: number; height: number };
type FrameDimension = { frameWidth: number; frameHeight: number };

export class AssetCatalogueService<A extends string, S extends string> {
  private gameAssets = new Map<A, GameAsset<A>>();
  private gameSprites = new Map<S, GameSprite<S>>();

  constructor(
    private assetEnum: Record<string, A>,
    private assetDimensions: Record<A, Dimension>,
    private spriteEnum?: Record<string, S>,
    private spriteDimensions?: Record<S, FrameDimension>,
  ) {
    this.buildAssetCatalogue();
    if (spriteEnum && spriteDimensions) {
      this.buildSpriteCatalogue();
    }
  }

  public preloadCatalogue(scene: Phaser.Scene, localAssetPath: string) {
    for (const type of this.gameAssets.keys()) {
      scene.load.image(type.toString(), `${localAssetPath}/${type}.png`);
    }

    this.gameSprites.forEach((value, type) => {
      scene.load.spritesheet(type.toString(), `${localAssetPath}/${type}.png`, {
        frameWidth: value.dimensions.frameWidth,
        frameHeight: value.dimensions.frameHeight,
      });
    });
  }

  public getAsset(type: A): GameAsset<A> {
    const asset = this.gameAssets.get(type);
    if (!asset) {
      throw new Error(`Asset undefined for type: ${type}`);
    }
    return asset;
  }

  public getSprite(type: S): GameSprite<S> {
    const sprite = this.gameSprites.get(type);
    if (!sprite) {
      throw new Error(`Sprite undefined for type: ${type}`);
    }
    return sprite;
  }

  private buildAssetCatalogue(): void {
    Object.values(this.assetEnum).forEach((type) => {
      const dimensions = this.assetDimensions[type];
      if (!dimensions) {
        throw new Error(`Undefined dimensions for asset type: ${type}`);
      }
      this.gameAssets.set(type, {
        type,
        dimensions,
      });
    });
  }

  private buildSpriteCatalogue(): void {
    if (!this.spriteEnum || !this.spriteDimensions) {
      return; // Skip if sprites are not defined
    }

    const spriteDimensions = this.spriteDimensions;
    Object.values(this.spriteEnum).forEach((type) => {
      const dimensions = spriteDimensions[type];
      if (!dimensions) {
        throw new Error(`Undefined dimensions for sprite type: ${type}`);
      }
      this.gameSprites.set(type, {
        type,
        dimensions,
      });
    });
  }
}
